import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  "Software Development",
  "Digital Transformation",
  "Cloud Services",
  "AI & Machine Learning",
  "Data Engineering",
  "Managed Services",
  "ERP/CRM",
  "CPQ/Enterprise Pricing",
  "Site Reliability Engineering",
  "Quality Assurance & Testing",
  "Dynamic Workforce Solutions",
  "Other"
];

const initialFormData = {
  name: "",
  email: "",
  company: "",
  phone: "",
  service_interest: "",
  message: ""
};

export function ContactForm() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState(initialFormData);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleServiceChange = (value) => {
    setFormData({ ...formData, service_interest: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully! We'll get back to you soon.");
    } catch (err) {
      toast.error("Failed to send message. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setSubmitted(false);
    setFormData(initialFormData);
  };

  return (
    <div className="lg:col-span-2">
      <div className="bg-white p-8 lg:p-12 border border-slate-200">
        {submitted ? (
          <div className="text-center py-12">
            <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-6" />
            <h2 className="text-2xl font-semibold text-slate-900 font-['Outfit'] mb-4">
              Thank You!
            </h2>
            <p className="text-slate-600 mb-8 font-['Plus_Jakarta_Sans']">
              Your message has been received. Our team will review it and get back 
              to you within 24 hours.
            </p>
            <Button
              onClick={handleReset}
              variant="outline"
              className="border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-none"
            >
              Send Another Message
            </Button>
          </div>
        ) : (
          <>
            <h2 className="text-xl font-semibold text-slate-900 font-['Outfit'] mb-6">
              Send Us a Message
            </h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="name" className="text-slate-700">Full Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="mt-1 rounded-none border-slate-300"
                    data-testid="contact-name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email" className="text-slate-700">Email *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="mt-1 rounded-none border-slate-300"
                    data-testid="contact-email"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <Label htmlFor="company" className="text-slate-700">Company</Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="mt-1 rounded-none border-slate-300"
                    data-testid="contact-company"
                  />
                </div>
                
                <div>
                  <Label htmlFor="phone" className="text-slate-700">Phone</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    className="mt-1 rounded-none border-slate-300"
                    data-testid="contact-phone"
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="service_interest" className="text-slate-700">
                  Service Interest
                </Label>
                <Select onValueChange={handleServiceChange} value={formData.service_interest}>
                  <SelectTrigger 
                    className="mt-1 rounded-none border-slate-300"
                    data-testid="contact-service"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service} value={service}>
                        {service}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="message" className="text-slate-700">Message *</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder="Tell us about your project or inquiry..."
                  className="mt-1 rounded-none border-slate-300"
                  data-testid="contact-message"
                />
              </div>
              
              <Button
                type="submit"
                disabled={submitting}
                className="w-full sm:w-auto bg-blue-700 hover:bg-blue-800 text-white rounded-none px-8"
                data-testid="contact-submit"
              >
                {submitting ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
